import React from 'react';
import { platforms } from '../config/platforms';
import { usePlatforms } from '../contexts/PlatformContext';
import { PlatformIcon } from './PlatformIcon';
import { ViewHeader } from './ViewHeader';
import { ActionFooter } from './ActionFooter';
import './PostPreviewModal.css';

type AttachmentItem = { type: 'image'; url?: string; preview?: string; alt?: string };
type PlatformKey = keyof typeof platforms;

interface PostPreviewModalProps {
  isOpen: boolean;
  originalText: string;
  editedText: string;
  reasoning: string;
  isLoading: boolean;
  error?: string | null;
  selectedOption: 'original' | 'edited';
  genres?: string[];
  selectedGenre?: string;
  onSelectGenre?: (genre: string) => void;
  attachments?: AttachmentItem[];
  userAvatarUrl?: string | null;
  showClarityEditing?: boolean;
  onSelectOption: (option: 'original' | 'edited') => void;
  onEditedTextChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  onBackToEditing?: (text: string) => void;
}

export const PostPreviewModal: React.FC<PostPreviewModalProps> = ({
  isOpen,
  originalText,
  editedText,
  reasoning,
  isLoading,
  error,
  selectedOption,
  genres = [],
  selectedGenre,
  onSelectGenre,
  attachments = [],
  userAvatarUrl,
  showClarityEditing = true,
  onSelectOption,
  onEditedTextChange,
  onClose,
  onConfirm,
  onBackToEditing
}) => {
  const { enabledPlatforms, togglePlatform } = usePlatforms();

  if (!isOpen) return null;

  const hasAttachments = attachments.length > 0;
  const showGenres = genres.length > 0 && selectedGenre && onSelectGenre;

  // Check if we have a generated edit (editedText differs from original)
  const hasGeneratedEdit = editedText !== originalText;

  // Build version options: Original + Edited (or genre variants)
  // If clarity editing is hidden, only show Original.
  // Otherwise show edited options when we have generated content (or are loading).
  const versionOptions = !showClarityEditing
    ? [{ key: 'original', label: 'Original' }]
    : showGenres
      ? [{ key: 'original', label: 'Original' }, ...genres.map(g => ({ key: g, label: g }))]
      : (isLoading || hasGeneratedEdit)
        ? [{ key: 'original', label: 'Original' }, { key: 'edited', label: isLoading ? 'Generating…' : 'Edit' }]
        : [{ key: 'original', label: 'Original' }];

  // When no genres, always use 'edited' as key; otherwise use the selected genre
  const currentVersionKey = selectedOption === 'original'
    ? 'original'
    : (showGenres ? selectedGenre : 'edited');
  const selectedText = selectedOption === 'original' ? originalText : editedText;

  // Only show diff when viewing edited version (compare edited to original)
  const showDiff = selectedOption === 'edited' && originalText !== editedText;
  const diffTokens = showDiff ? getWordDiffTokens(originalText, editedText) : [];

  const handleRevertToken = (tokenIndex: number) => {
    const token = diffTokens[tokenIndex];
    if (!token || token.type !== 'removed') return;
    const nextEditedText = applyRevertToken(diffTokens, tokenIndex);
    onEditedTextChange(nextEditedText);
  };

  const activePlatforms = (Object.keys(platforms) as PlatformKey[]).filter(key => enabledPlatforms[key]);

  const handleVersionSelect = (key: string) => {
    if (key === 'original') {
      onSelectOption('original');
    } else {
      onSelectOption('edited');
      if (showGenres && onSelectGenre) {
        onSelectGenre(key);
      }
    }
  };

  return (
    <div className="post-preview-overlay">
      <div className="post-preview-modal">
        <ViewHeader
          navLabel="Preview navigation"
          actions={[
            {
              icon: 'close',
              label: 'Close preview',
              onClick: onClose,
            },
          ]}
        />
        <div className="post-preview-scroll-area">
          <div className="post-preview-body">
            {/* Version Selector */}
            <div className="post-preview-version-selector">
              {versionOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`post-preview-version-pill${currentVersionKey === option.key ? ' active' : ''}${option.key === 'edited' && isLoading ? ' loading' : ''}`}
                  onClick={() => handleVersionSelect(option.key)}
                  disabled={option.key === 'edited' && isLoading}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Post Preview - Styled like social media */}
            <div className="post-preview-card">
              <div className="post-preview-card-header">
                {userAvatarUrl ? (
                  <img
                    src={userAvatarUrl}
                    alt="Profile"
                    className="post-preview-avatar"
                  />
                ) : (
                  <div className="post-preview-avatar post-preview-avatar-fallback">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </div>
                )}
                <div className="post-preview-meta">
                  <span className="post-preview-display-name">Preview</span>
                  <span className="post-preview-handle">@you</span>
                  <span className="post-preview-dot">·</span>
                  <span className="post-preview-time">now</span>
                </div>
              </div>
              <div className="post-preview-content">
                {diffTokens.length > 0 ? (
                  diffTokens.map((token, index) => (
                    <span
                      key={`${token.value}-${index}`}
                      className={`post-preview-diff-token ${token.type}${token.type === 'removed' && !token.isWhitespace ? ' clickable' : ''}`}
                      role={token.type === 'removed' && !token.isWhitespace ? 'button' : undefined}
                      tabIndex={token.type === 'removed' && !token.isWhitespace ? 0 : undefined}
                      onClick={token.type === 'removed' && !token.isWhitespace ? () => handleRevertToken(index) : undefined}
                      onKeyDown={token.type === 'removed' && !token.isWhitespace
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleRevertToken(index);
                            }
                          }
                        : undefined}
                    >
                      {token.value}
                    </span>
                  ))
                ) : (
                  <span>{selectedText || 'No content yet.'}</span>
                )}
              </div>
              {showDiff && (
                <p className="post-preview-diff-hint">Tap red text to restore it and remove the replacement.</p>
              )}
            </div>

            {/* Attachments */}
            {hasAttachments && (
              <div className="post-preview-attachments">
                {attachments.map((attachment, index) => (
                  <img
                    key={`${attachment.url || attachment.preview}-${index}`}
                    src={attachment.url || attachment.preview}
                    alt={attachment.alt || ''}
                    className="post-preview-attachment"
                  />
                ))}
              </div>
            )}

            {error && (
              <div className="post-preview-info-card post-preview-info-card-error">
                <span className="post-preview-reasoning-title">Edit unavailable</span>
                <p className="post-preview-error">{error}</p>
              </div>
            )}

            {reasoning && selectedOption === 'edited' && (
              <div className="post-preview-info-card">
                <span className="post-preview-reasoning-title">Why this edit</span>
                <p>{reasoning}</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer / Action Bar */}
        <ActionFooter position="sticky">
          <div className="post-preview-footer-left">
            <span className="post-preview-post-to-label">Post to</span>
            <div className="post-preview-platform-icons">
              {(Object.keys(platforms) as PlatformKey[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`post-preview-platform-btn${enabledPlatforms[key] ? ' active' : ''}`}
                  onClick={() => togglePlatform(key)}
                  title={`${enabledPlatforms[key] ? 'Disable' : 'Enable'} ${platforms[key].name}`}
                >
                  <PlatformIcon platformKey={key} icon={platforms[key].icon} size={20} />
                </button>
              ))}
            </div>
          </div>
          <div className="post-preview-footer-right">
            {onBackToEditing && (
              <button
                className="action-footer__button action-footer__button--secondary"
                onClick={() => onBackToEditing(selectedText)}
              >
                Edit
              </button>
            )}
            <button
              className="action-footer__button action-footer__button--primary"
              onClick={onConfirm}
              disabled={!selectedText.trim() || activePlatforms.length === 0}
            >
              Post
            </button>
          </div>
        </ActionFooter>

      </div>
    </div>
  );
};

type DiffToken = {
  value: string;
  type: 'same' | 'added' | 'removed';
  isWhitespace: boolean;
};

const tokenizeWords = (text: string) =>
  text.match(/\s+|\S+/g) || [];

const getWordDiffTokens = (originalText: string, editedText: string): DiffToken[] => {
  const original = tokenizeWords(originalText);
  const edited = tokenizeWords(editedText);
  const rows = original.length;
  const cols = edited.length;
  const table: number[][] = Array.from({ length: rows + 1 }, () => Array(cols + 1).fill(0));

  for (let i = 1; i <= rows; i += 1) {
    for (let j = 1; j <= cols; j += 1) {
      if (original[i - 1] === edited[j - 1]) {
        table[i][j] = table[i - 1][j - 1] + 1;
      } else {
        table[i][j] = Math.max(table[i - 1][j], table[i][j - 1]);
      }
    }
  }

  const tokens: DiffToken[] = [];
  let i = rows;
  let j = cols;
  while (i > 0 && j > 0) {
    if (original[i - 1] === edited[j - 1]) {
      tokens.unshift({ value: original[i - 1], type: 'same', isWhitespace: /^\s+$/.test(original[i - 1]) });
      i -= 1;
      j -= 1;
    } else if (table[i - 1][j] >= table[i][j - 1]) {
      tokens.unshift({ value: original[i - 1], type: 'removed', isWhitespace: /^\s+$/.test(original[i - 1]) });
      i -= 1;
    } else {
      tokens.unshift({ value: edited[j - 1], type: 'added', isWhitespace: /^\s+$/.test(edited[j - 1]) });
      j -= 1;
    }
  }

  while (i > 0) {
    tokens.unshift({ value: original[i - 1], type: 'removed', isWhitespace: /^\s+$/.test(original[i - 1]) });
    i -= 1;
  }

  while (j > 0) {
    tokens.unshift({ value: edited[j - 1], type: 'added', isWhitespace: /^\s+$/.test(edited[j - 1]) });
    j -= 1;
  }

  return tokens;
};

const applyRevertToken = (tokens: DiffToken[], removedIndex: number) => {
  const output: string[] = [];
  const replacementRange = findReplacementRange(tokens, removedIndex);

  tokens.forEach((token, index) => {
    if (index === removedIndex && token.type === 'removed') {
      output.push(token.value);
      return;
    }

    if (token.type === 'same') {
      output.push(token.value);
      return;
    }

    if (token.type === 'added') {
      if (replacementRange && index >= replacementRange.start && index <= replacementRange.end) {
        return;
      }
      output.push(token.value);
    }
  });

  return output.join('');
};

const findReplacementRange = (tokens: DiffToken[], removedIndex: number) => {
  const nextRange = getContiguousRange(tokens, removedIndex + 1, 'added', 1);
  if (nextRange) {
    return nextRange;
  }
  return getContiguousRange(tokens, removedIndex - 1, 'added', -1);
};

const getContiguousRange = (
  tokens: DiffToken[],
  startIndex: number,
  type: DiffToken['type'],
  direction: 1 | -1
) => {
  if (startIndex < 0 || startIndex >= tokens.length) return null;
  if (tokens[startIndex]?.type !== type) return null;

  let index = startIndex;
  while (index + direction >= 0 && index + direction < tokens.length && tokens[index + direction].type === type) {
    index += direction;
  }

  return direction === 1
    ? { start: startIndex, end: index }
    : { start: index, end: startIndex };
};
